import React, { useMemo, useState } from "react";
import { FiChevronDown } from "react-icons/fi";
import { IoIosCloseCircle } from "react-icons/io";
import Filters from "./Filters";

const getOptionTextLabel = (option) => {
  if (typeof option?.labelKey === "string" && option.labelKey.trim()) {
    return option.labelKey;
  }
  if (typeof option?.label === "string" && option.label.trim()) {
    return option.label;
  }
  if (typeof option?.title === "string" && option.title.trim()) {
    return option.title;
  }
  if (typeof option?.name === "string" && option.name.trim()) {
    return option.name;
  }
  if (typeof option?.username === "string" && option.username.trim()) {
    return option.username;
  }
  if (option?.value != null && typeof option?.value !== "object") {
    return String(option.value);
  }
  return "";
};

const normalizeOption = (option) => ({
  ...option,
  value: option?.value ?? option?.id ?? option?.title ?? "",
  label: getOptionTextLabel(option),
});

const isActiveFilter = (filter) => {
  const value = filter?.value;
  if (!filter?.filter_type) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (value && typeof value === "object") {
    return Object.values(value).some(
      (item) => item !== undefined && item !== null && item !== "",
    );
  }
  return value !== undefined && value !== null && value !== "";
};

const buildDisplayLabel = (column, rawValue) => {
  if (rawValue == null) return "";
  if (rawValue === "in_progress") return "In Progress";

  const resolvedRawValue =
    typeof rawValue === "object"
      ? rawValue.labelKey ||
        (typeof rawValue.label === "string" ? rawValue.label : null) ||
        rawValue.title ||
        rawValue.name ||
        rawValue.value ||
        ""
      : rawValue;

  const normalizedOptions = (column?.options || []).map(normalizeOption);
  const optionMatch = normalizedOptions.find(
    (option) =>
      String(option.value) === String(resolvedRawValue) ||
      (typeof option.label === "string" &&
        option.label.toLowerCase() === String(resolvedRawValue).toLowerCase()),
  );

  if (optionMatch && typeof optionMatch.label === "string" && optionMatch.label) {
    return optionMatch.label;
  }

  return typeof resolvedRawValue === "string"
    ? resolvedRawValue
    : String(resolvedRawValue ?? "");
};

const MobileListingFilters = ({
  columns = [],
  filters,
  searchFilters = {},
  setSearchFilters,
}) => {
  const [openFilter, setOpenFilter] = useState(null);

  const filterColumns = useMemo(
    () =>
      Array.isArray(filters) && filters.length > 0
        ? filters
        : (columns || [])
            .filter((column) => column?.tableColumn === true)
            .map((column) => ({
              ...column,
              name: column?.filterLabel || column?.name,
            })),
    [columns, filters],
  );

  const removeFilter = (filterName) => {
    if (!setSearchFilters) return;
    setSearchFilters((previous = {}) => {
      const updated = { ...previous };
      delete updated[filterName];
      return updated;
    });
  };

  if (filterColumns.length === 0) return null;

  return (
    <>
      <div className="mobile_filter_pills_scroll">
        {filterColumns.map((column, columnIndex) => {
          const filterName = column.filterName;
          const filter = searchFilters?.[filterName];
          const isActive = isActiveFilter(filter);
          const label = column?.filterLabel || column?.name || filterName;

          const filterValueLabel =
            filter?.filter_type === "date_range" &&
            typeof filter?.value === "object"
              ? `${filter?.value?.startDate || ""} to ${filter?.value?.endDate || ""}`
              : filter?.filter_type === "one_of"
              ? Array.isArray(filter.value)
                ? filter.value
                    .map((val) => buildDisplayLabel(column, val))
                    .join(", ")
                : buildDisplayLabel(column, filter.value)
              : buildDisplayLabel(column, filter?.value);

          return isActive ? (
            <div
              className="mobile_filter_pill mobile_filter_pill_active"
              key={filterName || `${label}-${columnIndex}`}
              onClick={() => setOpenFilter(column)}
            >
              <span>
                {label}: {filter.filter_type?.replace(/_/g, " ")} "{filterValueLabel}"
              </span>
              <IoIosCloseCircle
                className="mobile_filter_pill_remove"
                onClick={(event) => {
                  event.stopPropagation();
                  removeFilter(filterName);
                }}
              />
            </div>
          ) : (
            <div
              className="mobile_filter_pill"
              key={filterName || `${label}-${columnIndex}`}
              onClick={() => setOpenFilter(column)}
            >
              <span>{label}</span>
              <FiChevronDown className="pill_arrow_svg" />
            </div>
          );
        })}
      </div>

      {openFilter && (
        <div
          className="mobile_filter_overlay"
          onClick={() => setOpenFilter(null)}
        >
          <div
            className="mobile_filter_sheet"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mobile_filter_sheet_header">
              <span>
                {openFilter?.filterLabel || openFilter?.name || "Filter"}
              </span>
              <button type="button" onClick={() => setOpenFilter(null)}>
                ✕
              </button>
            </div>
            <div className="mobile_filter_sheet_body">
              {openFilter.filterComponent ? (
                <openFilter.filterComponent
                  options={openFilter.options}
                  isMulti={openFilter.isMulti}
                  filterName={openFilter.filterName}
                  searchFilters={searchFilters}
                  setSearchFilters={setSearchFilters}
                  closeFilter={() => setOpenFilter(null)}
                  columnName={openFilter.name}
                  setWrapperRef={() => {}}
                />
              ) : (
                <Filters
                  closeFilter={() => setOpenFilter(null)}
                  searchFilters={searchFilters}
                  setSearchFilters={setSearchFilters}
                  filterName={openFilter.filterName}
                  type={openFilter.type}
                  operatorOptions={openFilter.operatorOptions}
                  component={openFilter.component || null}
                  options={openFilter.options}
                  setWrapperRef={() => {}}
                  removeFilter={() => {
                    removeFilter(openFilter.filterName);
                    setOpenFilter(null);
                  }}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MobileListingFilters;
